import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Descriptions, Table, Tag, Button, Space, useApp, Typography, Row, Col, Statistic, Tabs,
  Timeline, Empty, Steps, Divider, Tooltip, Modal, Input, Popconfirm, theme, Spin, Result, Alert,
} from '../../ui';
import type { ButtonProps } from '../../ui';
import {
  ShoppingCart, CheckCircle2, CalendarCheck, Inbox, Car, Flag,
  FileCheck, Ban, FileText, Clock, FlaskConical, User,
  Send, XCircle, Repeat, Undo2, ArrowLeftRight, Upload, Wallet, MapPin,
} from 'lucide-react';
import { api, request } from '../../api/client';
import { RaiseReturnModal } from './RaiseReturnModal';
import { CreditNoteDrawer } from '../../components/CreditNoteDrawer';
import { usePermissionChecker, useAuthStore } from '../../store/auth';
import { brand } from '../../assets/brand';
import { fmtQty } from '../../lib/format';
import { boxLoosePhrase } from '../../lib/packaging';
import { DispatchModal } from './DispatchModal';
import CancelOrderModal, { CancelBreakdown } from './CancelOrderModal';
import { StockPlanPanel } from './StockPlanPanel';
import { WarehouseCoveragePanel } from './WarehouseCoveragePanel';
import { FULFILLMENT_V2_ENABLED, RETURNS_LIFECYCLE_ENABLED } from '../../lib/flags';
import { ApproveOrderModal } from './ApproveOrderModal';
import { PayOrderModal } from './PayOrderModal';
import { RefundPaymentModal } from './RefundPaymentModal';
import { SettleAdvanceModal } from './SettleAdvanceModal';
import { AdvanceCdPanel } from './AdvanceCdPanel';
import { InvoiceUploadModal } from '../billing/InvoiceUploadModal';
import { FileUploadField } from '../../components/FileUploadField';
import { formatIst } from '../../lib/time';

type GatedButtonProps = ButtonProps & { allowed: boolean; reason: string };

/** A Button that, when the user lacks the required permission, renders disabled
 *  with a tooltip explaining why — instead of letting the click hit the API and
 *  bounce back as a raw "Missing permission(s)" error toast. */
function GatedButton({ allowed, reason, children, style, ...rest }: GatedButtonProps) {
  if (allowed) return <Button {...rest} style={style}>{children}</Button>;
  return (
    <Tooltip label={reason}>
      <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
        <Button {...rest} disabled style={{ ...style, pointerEvents: 'none' }}>
          {children}
        </Button>
      </span>
    </Tooltip>
  );
}

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default', PLACED: 'blue', AWAITING_PRODUCTION: 'magenta', IN_PRODUCTION: 'purple', AWAITING_TRANSFER: 'geekblue', APPROVED: 'cyan', ACCEPTED: 'lime', PACKED: 'gold', DISPATCHED: 'orange',
  DELIVERED: 'green', INVOICED: 'purple', CANCELLED: 'red', CLOSED: 'default',
  ON_HOLD: 'orange', CORRECTION_REQUIRED: 'gold', REJECTED: 'red', BACKORDER: 'volcano',
};

// Humanize a status enum for display: IN_PRODUCTION → "In Production".
const statusLabel = (s: string) => (s ?? '').replace(/_/g, ' ');

const STATUS_ICON: Record<string, JSX.Element> = {
  DRAFT: <FileText size={16} />,
  PLACED: <ShoppingCart size={16} />,
  AWAITING_PRODUCTION: <Clock size={16} />,
  IN_PRODUCTION: <FlaskConical size={16} />,
  AWAITING_TRANSFER: <Clock size={16} />,
  APPROVED: <CheckCircle2 size={16} />,
  ACCEPTED: <CalendarCheck size={16} />,
  PACKED: <Inbox size={16} />,
  DISPATCHED: <Car size={16} />,
  DELIVERED: <Flag size={16} />,
  INVOICED: <FileCheck size={16} />,
  CANCELLED: <Ban size={16} />,
  CLOSED: <CheckCircle2 size={16} />,
  ON_HOLD: <Clock size={16} />,
  CORRECTION_REQUIRED: <FileText size={16} />,
  REJECTED: <XCircle size={16} />,
  BACKORDER: <Clock size={16} />,
};

const NEXT: Record<string, string[]> = {
  PLACED: ['APPROVED', 'CANCELLED'],
  AWAITING_PRODUCTION: ['APPROVED', 'PACKED', 'CANCELLED'],
  // Being produced (a run exists). Reverts/heals automatically; manual options are forward + cancel.
  IN_PRODUCTION: ['APPROVED', 'PACKED', 'CANCELLED'],
  // Held for consolidation: no manual transition — it self-heals back when transfers land.
  // (Cancel is offered via the dedicated "Cancel consolidation" button.)
  AWAITING_TRANSFER: [],
  APPROVED: ['ACCEPTED', 'CANCELLED'],
  // Packing is done via the "Pick & pack" batch picker (it commits stock), not a bare status flip.
  ACCEPTED: ['CANCELLED'],
  PACKED: ['DISPATCHED', 'CANCELLED'],
  DISPATCHED: ['DELIVERED', 'CANCELLED'],
  // New flow closes directly (invoice already raised before dispatch); legacy goes
  // via INVOICED. The CLOSED-from-DELIVERED button is filtered to new-flow orders below.
  DELIVERED: ['INVOICED', 'CLOSED'],
  INVOICED: ['CLOSED'],
  // New approval sub-states. The reason-based reject/hold/correction live on the Approval Queue;
  // these generic transitions let a held / backordered order move forward from the detail page.
  ON_HOLD: ['APPROVED', 'CANCELLED'],
  CORRECTION_REQUIRED: ['CANCELLED'],
  BACKORDER: ['APPROVED', 'CANCELLED'],
  REJECTED: [],
};

const STEPS = ['PLACED', 'APPROVED', 'ACCEPTED', 'PACKED', 'DISPATCHED', 'DELIVERED', 'INVOICED', 'CLOSED'];

// Friendlier labels for the status-advance buttons.
const ACTION_LABEL: Record<string, string> = {
  APPROVED: 'Approve',
  ACCEPTED: 'Accept order',
  PACKED: 'Pick & pack',
  DELIVERED: 'Mark Delivered',
  INVOICED: 'Mark Invoiced',
  CLOSED: 'Close order',
  CANCELLED: 'Cancel',
};

const inr = (v: any) =>
  `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
// Format a plain 'YYYY-MM-DD' (e.g. batch expiry) as '27 Jun 2029' without timezone shifting.
const fmtDateShort = (d?: string | null) => {
  if (!d) return '';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1];
  return mon ? `${dd} ${mon} ${y}` : String(d);
};
// Compact elapsed-time label for the fulfilment-timing panel: '12 min', '2h 8m', '1d 3h'.
const fmtDuration = (ms: number) => {
  if (!ms || ms < 0) return '—';
  const min = Math.round(ms / 60000);
  if (min < 1) return '<1 min';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24), hh = h % 24;
  return hh ? `${d}d ${hh}h` : `${d}d`;
};

const SHIPMENT_COLOR: Record<string, string> = {
  PACKED: 'gold', DISPATCHED: 'orange', DELIVERED: 'green', CANCELLED: 'red',
};

// Stateful POD picker for the "Mark delivered" dialog. Modal.confirm renders its content
// once, so a bare closure value never re-renders — this holds the uploaded URL in state so
// the FileUploadField shows its file chip AND we render a clear attached/not-attached status.
function PodPicker({ onChange }: { onChange: (url: string) => void }) {
  const [url, setUrl] = useState('');
  return (
    <div style={{ marginTop: 8 }}>
      <div className="text-xs" style={{ color: '#8c8c8c', marginBottom: 6 }}>
        Optional: attach POD (proof of delivery) — signed challan photo or PDF.
      </div>
      <FileUploadField
        category="pod"
        buttonLabel={url ? 'Replace POD' : 'Upload POD'}
        value={url}
        onChange={(u) => { setUrl(u); onChange(u); }}
      />
      <div className="text-sm" style={{ marginTop: 8 }}>
        {url
          ? <span style={{ color: '#389e0d' }}><CheckCircle2 size={16} /> POD attached</span>
          : <span style={{ color: '#bfbfbf' }}><XCircle size={16} /> No POD attached yet</span>}
      </div>
    </div>
  );
}

// Blob (file) downloads return a Blob body even on error, so a JSON {error:{message}} can't be read
// off e.response.data directly — this unwraps it to the real server message (e.g. why a proforma failed).
async function readDownloadError(e: any, fallback: string): Promise<string> {
  const data = e?.response?.data;
  try {
    if (data instanceof Blob) {
      const txt = await data.text();
      try { return JSON.parse(txt)?.error?.message ?? fallback; } catch { return fallback; }
    }
    return data?.error?.message ?? fallback;
  } catch { return fallback; }
}

export function OrderDetailPage() {
  const { token } = theme.useToken();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { message } = useApp();
  const qc = useQueryClient();
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [refundFor, setRefundFor] = useState<any>(null);
  const [settleOpen, setSettleOpen] = useState(false);
  const [invoiceUploadOpen, setInvoiceUploadOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [invoiceForShipment, setInvoiceForShipment] = useState<any | null>(null); // per-shipment (PER_SHIPMENT) invoice upload
  const [invoicePickerOpen, setInvoicePickerOpen] = useState(false); // header shortcut when >1 packed shipment awaits an invoice
  const [activeTab, setActiveTab] = useState('overview');
  const [overrideFor, setOverrideFor] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState('');

  const { data: o, isLoading, isError } = useQuery({
    queryKey: ['order', id],
    queryFn: () => request<any>({ url: `/orders/${id}` }),
  });

  const { data: timeline } = useQuery({
    queryKey: ['order-timeline', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/timeline` }),
    enabled: !!id,
  });

  const { data: invoices } = useQuery({
    queryKey: ['order-invoices', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/invoices` }),
    enabled: !!id,
  });

  const { data: shipments } = useQuery({
    queryKey: ['order-shipments', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/shipments` }),
    enabled: !!id,
  });

  // Credit / debit notes raised anywhere on this order (across its invoices + sales returns) — for traceability.
  const { data: orderNotes } = useQuery({
    queryKey: ['order-credit-notes', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/credit-notes` }),
    enabled: !!id,
  });
  const [noteDrawerId, setNoteDrawerId] = useState<string | null>(null);

  // Remaining (un-shipped) quantity per order item — used to decide whether
  // another dispatch is still possible.
  const { data: pending } = useQuery({
    queryKey: ['order-pending', id],
    queryFn: () => request<Record<string, number>>({ url: `/orders/${id}/shipments/pending` }),
    enabled: !!id,
  });

  // Resolve ids → names for a professional Overview (permission-gated; falls
  // back to #id when a role can't fetch the master list).
  const hasPerm = usePermissionChecker();
  // Fulfillment roles (Warehouse Manager) don't see financials — hide money panels, totals,
  // per-line prices/taxes and the Payments tab. Consistent with their no-PAYMENT.* permission set.
  const userRole = useAuthStore((s) => s.user?.role);
  const canSeeMoney = userRole !== 'WAREHOUSE_MANAGER';
  // Action gating — mirrors the backend @RequirePermissions on each endpoint, so
  // a role only ever sees an action it can actually perform (disabled + reason
  // otherwise, instead of a raw 403 after the click).
  const canEdit = hasPerm('ORDER.EDIT'); // dispatch, cancel shipment
  const canPack = hasPerm('WAREHOUSE.PACK.COMPLETE') || canEdit; // WM (or admin) packs + dispatches their warehouse's orders
  const canAccept = hasPerm('WAREHOUSE.PACK.START') || canEdit; // WM (or admin) accepts the routed order
  // Dispatch planning (warehouse selection / stock plan / consolidation) — granular perm OR ORDER.EDIT.
  const canPlan = hasPerm('ORDER.DISPATCH.PLAN') || canEdit;
  const canAddStock = hasPerm('STOCK.MASTER.ADD'); // Phase 2 — "Add stock" → master-warehouse stock (auto-GRN)
  const canReportShortfall = hasPerm('WAREHOUSE.PACK.START') || hasPerm('WAREHOUSE.PACK.COMPLETE') || hasPerm('ORDER.DISPATCH.PLAN') || hasPerm('ORDER.EDIT'); // Phase 2
  const canDeliver = hasPerm('ORDER.DELIVER'); // mark delivered (field action — SP/SH too)
  const canInvoice = hasPerm('INVOICE.CREATE');
  // A Warehouse Manager can also upload/verify the Tally invoice for their own warehouse's order.
  const canUploadInvoice = canInvoice || hasPerm('WAREHOUSE.PACK.COMPLETE');
  // Granular Tally-invoice gate (mirrors the backend): VERIFY falls back to INVOICE.CREATE; OVERRIDE is admin-only.
  const canVerify = hasPerm('INVOICE.VERIFY') || canInvoice || hasPerm('WAREHOUSE.PACK.COMPLETE');
  const canOverride = hasPerm('INVOICE.OVERRIDE');
  const canVoidInvoice = hasPerm('INVOICE.CANCEL'); // void a recorded Tally invoice
  const canProforma = hasPerm('ORDER.PROFORMA.VIEW'); // proforma download — staff + the order's own retailer
  const canCreate = hasPerm('ORDER.CREATE'); // reorder
  // PATCH /orders/:id/status is gated by ORDER.APPROVE OR ORDER.EDIT.
  const canChangeStatus = hasPerm('ORDER.APPROVE') || canEdit;
  const canCancel = hasPerm('ORDER.CANCEL') || canChangeStatus; // retailer / sales person / sales head can cancel too
  const canPay = hasPerm('ORDER.PAY'); // retailer self-pay, or SP/SH on behalf — submits a payment for the order
  const canRefund = hasPerm('PAYMENT.REFUND'); // Accounts/Admin/SA — record a refund of an advance on a cancelled order
  const canLockCd = hasPerm('ORDER.CD.LOCK'); // Accounts/Admin/SA — lock the advance cash discount before Tally billing
  // A return is PLACED only by the retailer / SP / SH (per the spec) — approvers (Admin/SA), Accounts and the
  // WHM don't raise returns, so gate on the raiser role rather than the bare permission (SUPER_ADMIN holds
  // every perm via the wildcard, which would otherwise show the button to the approver).
  const canRaiseReturn = ['RETAILER', 'SALES_PERSON', 'SALES_HEAD'].includes(userRole ?? '');
  const { data: paySummary } = useQuery({
    queryKey: ['order-pay-summary', id],
    queryFn: () => request<any>({ url: `/orders/${id}/payment-summary` }),
    enabled: !!id, // also drives the order-based close gate / due display (all viewers, not just payers)
  });
  // Advance excess left after a short-close (retailer paid for more than the order was finally billed).
  // Accounts settle it (refund or keep-as-credit). Fetched for money-viewers who can see/act on refunds.
  const canViewAdvance = canRefund || hasPerm('PAYMENT.VIEW');
  const { data: advanceExcess } = useQuery({
    queryKey: ['order-advance-excess', id],
    queryFn: () => request<any>({ url: `/orders/${id}/advance-excess` }),
    enabled: !!id && canSeeMoney && canViewAdvance,
  });
  // This order's payments + their approval status — the retailer's payment history for the order.
  const { data: orderPayments } = useQuery({
    queryKey: ['order-payments', id],
    queryFn: () => request<any[]>({ url: `/orders/${id}/payments` }),
    enabled: !!id,
  });
  const { data: retailers } = useQuery({
    queryKey: ['order-detail-retailers'],
    queryFn: () => request<any>({ url: '/retailers', params: { pageSize: 200 } }),
    enabled: hasPerm('RETAILER.VIEW'),
  });
  const { data: users } = useQuery({
    queryKey: ['order-detail-users'],
    queryFn: () => request<any>({ url: '/users', params: { pageSize: 200 } }),
    enabled: hasPerm('USER.VIEW'),
  });
  const { data: warehouses } = useQuery({
    queryKey: ['order-detail-warehouses'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: hasPerm('WAREHOUSE.VIEW'),
  });
  const retailerName = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of retailers?.data ?? []) m.set(String(r.id), `${r.companyName} (${r.code})`);
    return m;
  }, [retailers]);
  const userName = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users?.data ?? []) m.set(String(u.id), u.fullName);
    return m;
  }, [users]);
  const warehouseName = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of warehouses ?? []) m.set(String(w.id), `${w.name} (${w.code})`);
    return m;
  }, [warehouses]);
  // Product names come straight from the order's own items (no extra fetch / permission needed),
  // so shipment-line drilldowns show names instead of raw product IDs for every viewer.
  const productName = useMemo(() => {
    const m = new Map<string, string>();
    for (const it of ((o as any)?.items ?? [])) {
      m.set(String(it.productId), `${it.productNameSnapshot}${it.variantLabel ? ` ${it.variantLabel}` : ''}`);
    }
    return m;
  }, [o]);
  const nameOf = (map: Map<string, string>, idv: any) =>
    idv ? (map.get(String(idv)) ?? `#${idv}`) : '—';

  // Full lifecycle for the Timeline tab: order status changes + advance/verify/reject/refund payment
  // events + invoices, merged in chronological order — so anyone (even a newbie) can trace the order
  // end-to-end at a glance, with who/when/amount and a proof link on each step.
  const lifecycleEvents = useMemo(() => {
    const ev: Array<{ at: any; color: string; icon: JSX.Element; title: any; who?: string | null; note?: any; noteStrong?: boolean; proof?: string | null }> = [];
    for (const t of (timeline ?? [])) {
      // A partial dispatch/delivery keeps the order OPEN — the backend writes those rows with NO "from"
      // status (a completing dispatch/delivery carries a real PACKED→ / DISPATCHED→). Render them as an
      // amber "Partially dispatched/delivered" so they don't read like the whole order is done.
      const isPartialShip = !t.fromStatus && (t.toStatus === 'DISPATCHED' || t.toStatus === 'DELIVERED');
      const baseIcon = STATUS_ICON[t.toStatus] ?? <Clock size={16} />;
      ev.push({
        at: t.at,
        color: isPartialShip ? '#faad14' : (STATUS_COLOR[t.toStatus] === 'red' ? 'red' : STATUS_COLOR[t.toStatus] === 'green' ? 'green' : 'blue'),
        icon: isPartialShip ? <span style={{ color: '#faad14' }}>{baseIcon}</span> : baseIcon,
        title: isPartialShip
          ? <b style={{ color: '#d48806' }}>{t.toStatus === 'DISPATCHED' ? 'Partially dispatched' : 'Partially delivered'}</b>
          : <span>{t.fromStatus ? <span style={{ color: '#999' }}>{statusLabel(t.fromStatus)} → </span> : null}<b>{statusLabel(t.toStatus)}</b></span>,
        who: t.changedByName, note: t.notes, noteStrong: isPartialShip,
      });
    }
    for (const p of (orderPayments ?? [])) {
      if (p.fromCredit) {
        // Applied account credit — no cash, no approval step; it settles the order immediately.
        ev.push({
          at: p.createdAt ?? p.paymentDate, color: 'green', icon: <Wallet size={16} />,
          title: <span><b>Account credit applied</b> · {inr2(p.amount)} <Tag color="green" style={{ margin: 0 }}>Credit</Tag></span>,
          who: p.submittedByName,
        });
        continue;
      }
      ev.push({
        at: p.createdAt ?? p.paymentDate, color: 'blue', icon: <Wallet size={16} />,
        title: <span>Advance <b>submitted</b> · {inr2(p.amount)} <Tag style={{ margin: 0 }}>{p.mode}</Tag></span>,
        who: p.submittedByName, note: p.approvalStatus === 'PENDING' ? 'Awaiting verification' : undefined,
        proof: p.attachmentUrls?.[0] ?? p.attachmentUrl ?? null,
      });
      if (p.approvalStatus === 'APPROVED' && p.approvedAt) {
        ev.push({ at: p.approvedAt, color: 'green', icon: <CheckCircle2 size={16} />,
          title: <span>Advance <b>verified</b> · {inr2(p.amount)}</span>, who: p.approvedByName });
      }
      if (p.approvalStatus === 'REJECTED') {
        ev.push({ at: p.createdAt ?? p.paymentDate, color: 'red', icon: <XCircle size={16} />,
          title: <span>Advance <b>rejected</b> · {inr2(p.amount)}</span>, note: p.rejectedReason });
      }
      if (p.status === 'REFUNDED' && p.refundedAt) {
        ev.push({ at: p.refundedAt, color: 'red', icon: <Repeat size={16} />,
          title: <span>Refund <b>processed</b> · {inr2(p.refundAmount ?? p.amount)} <Tag style={{ margin: 0 }}>{p.refundMode}</Tag></span>,
          who: p.refundedByName, note: p.refundReason, proof: p.refundProofUrls?.[0] ?? null });
      }
    }
    for (const i of (invoices ?? [])) {
      ev.push({
        at: i.invoiceDate, color: i.status === 'CANCELLED' ? 'red' : 'blue', icon: <FileCheck size={16} />,
        title: i.status === 'CANCELLED'
          ? <span>Tax invoice <b>{i.invoiceNo}</b> voided</span>
          : <span>Tax invoice <b>{i.invoiceNo}</b> raised · {inr2(i.grandTotal)}</span>,
      });
    }
    return ev.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  }, [timeline, orderPayments, invoices]);

  const deliverShipment = useMutation({
    mutationFn: ({ sid, podUrl }: { sid: string; podUrl?: string }) =>
      request({ url: `/orders/${id}/shipments/${sid}/deliver`, method: 'POST', data: { podUrl } }),
    onSuccess: () => {
      message.success('Marked delivered');
      qc.invalidateQueries({ queryKey: ['order-shipments', id] });
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // Dispatch (release): flip the packed shipment(s) to DISPATCHED. No batch/stock work — that
  // happened at pack. Logistics were captured in the pack modal; this just sends the goods.
  const dispatch = useMutation({
    mutationFn: () => request({ url: `/orders/${id}/shipments/dispatch`, method: 'POST', data: {} }),
    onSuccess: () => {
      message.success('Order dispatched');
      qc.invalidateQueries({ queryKey: ['order-shipments', id] });
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-pending', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Dispatch failed'),
  });

  const reorder = useMutation({
    mutationFn: () => request<any>({ url: `/orders/from/${id}`, method: 'POST' }),
    onSuccess: (newOrder: any) => {
      message.success(`Reorder ${newOrder.orderNo} placed`);
      navigate(`/orders/${newOrder.id}`);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Reorder failed'),
  });

  const cancelShipment = useMutation({
    mutationFn: ({ sid, reason }: { sid: string; reason: string }) =>
      request({ url: `/orders/${id}/shipments/${sid}/cancel`, method: 'POST', data: { reason } }),
    onSuccess: () => {
      message.success('Shipment cancelled, stock returned');
      qc.invalidateQueries({ queryKey: ['order-shipments', id] });
      qc.invalidateQueries({ queryKey: ['order-pending', id] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const askDeliver = (sid: string) => {
    let pod = '';
    Modal.confirm({
      title: 'Mark this shipment delivered?',
      width: 480,
      content: <PodPicker onChange={(url) => { pod = url; }} />,
      onOk: () => deliverShipment.mutate({ sid, podUrl: pod || undefined }),
    });
  };

  const askCancel = (sid: string) => {
    let reason = '';
    Modal.confirm({
      title: 'Cancel this shipment?',
      content: (
        <div style={{ marginTop: 8 }}>
          <div className="text-xs" style={{ color: token.colorTextSecondary, marginBottom: 6 }}>Stock for these lines will be returned. Reason:</div>
          <Input.TextArea rows={2} onChange={(e) => (reason = e.target.value)} />
        </div>
      ),
      okType: 'danger',
      onOk: () => cancelShipment.mutate({ sid, reason: reason || 'Cancelled by user' }),
    });
  };

  // Shipments still awaiting delivery (drives the header "Mark Delivered" action).
  const openShipments = (shipments ?? []).filter((s: any) => s.status === 'DISPATCHED');
  const hasPackedShipment = (shipments ?? []).some((s: any) => s.status === 'PACKED');
  // PER_SHIPMENT shipments that still need their own Tally invoice (mirrors the per-shipment panel's
  // per-row gate) — drives the header "Upload invoice" shortcut + the pick-which-dispatch modal.
  const shipmentsNeedingInvoice = (shipments ?? []).filter((s: any) =>
    s.status !== 'CANCELLED'
    && ['PACKED', 'DISPATCHED', 'DELIVERED'].includes(s.status)
    && !(invoices ?? []).some((i: any) => String(i.shipmentId) === String(s.id) && i.status !== 'CANCELLED'));
  // Allocated batches per order line, from the order's non-cancelled shipments. batchNo/expiry are
  // present only for internal staff (the API omits them for retailers), so this shows nothing to them.
  const batchesByLine = useMemo(() => {
    const m = new Map<string, { batchNo: string; qty: number; expiry?: string | null }[]>();
    for (const s of (shipments ?? [])) {
      if (s.status === 'CANCELLED') continue;
      for (const it of (s.items ?? [])) {
        if (!it.batchNo) continue;
        const key = String(it.orderItemId);
        const arr = m.get(key) ?? [];
        arr.push({ batchNo: it.batchNo, qty: Number(it.qty), expiry: it.expiry });
        m.set(key, arr);
      }
    }
    return m;
  }, [shipments]);
  const handleMarkDelivered = () => {
    if (openShipments.length === 1) {
      askDeliver(openShipments[0].id);
    } else {
      // More than one open shipment — let the user choose which to deliver.
      setActiveTab('docs');
      message.info('This order has multiple shipments — mark each one delivered in the Documents tab.');
    }
  };

  const cancelConsolidation = useMutation({
    mutationFn: () => request({ url: `/orders/${id}/consolidate/cancel`, method: 'POST' }),
    onSuccess: () => {
      message.success('Consolidation cancelled — stock returned to source warehouses');
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to cancel consolidation'),
  });

  const [approveOpen, setApproveOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const change = useMutation({
    mutationFn: (status: string) => request({ url: `/orders/${id}/status`, method: 'PATCH', data: { status } }),
    onSuccess: () => {
      message.success('Status changed');
      setCancelModalOpen(false);
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  // Short-close: drop the un-shipped remainder of a partially fulfilled order (Admin/SA). The dispatched
  // part stays; the order total is revised to what was actually fulfilled (advance settled in Accounts).
  const shortClose = useMutation({
    mutationFn: (reason: string) => request({ url: `/orders/${id}/cancel-remainder`, method: 'POST', data: reason ? { reason } : {} }),
    onSuccess: () => {
      message.success('Pending units short-closed — settle any advance excess in Accounts');
      setCancelModalOpen(false);
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-shipments', id] });
      qc.invalidateQueries({ queryKey: ['order-pending', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Short-close failed'),
  });

  // Void (cancel) a recorded Tally invoice — reverses the ledger so a corrected one
  // can be re-uploaded, or so the order can be cancelled.
  const voidInvoice = useMutation({
    mutationFn: (invId: string) => request({ url: `/invoices/${invId}/void`, method: 'POST', data: {} }),
    onSuccess: () => {
      message.success('Invoice voided');
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-invoices', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['outstanding-aging'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to void invoice'),
  });

  // Re-run the proforma-vs-Tally comparison, or (admin) approve a mismatch to unblock dispatch.
  const refreshInv = () => {
    qc.invalidateQueries({ queryKey: ['order', id] });
    qc.invalidateQueries({ queryKey: ['order-invoices', id] });
  };
  const verifyInvoice = useMutation({
    mutationFn: (invId: string) => request<any>({ url: `/invoices/${invId}/verify`, method: 'POST', data: {} }),
    onSuccess: (r: any) => {
      if (r?.status === 'VERIFIED') message.success('Invoice verified — matches the order');
      else message.warning('Invoice does NOT match the order — approve the mismatch to dispatch');
      refreshInv();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const overrideInvoice = useMutation({
    mutationFn: ({ invId, reason }: { invId: string; reason: string }) => request({ url: `/invoices/${invId}/verify-override`, method: 'POST', data: { reason } }),
    onSuccess: () => { message.success('Mismatch approved — dispatch unblocked'); setOverrideFor(null); setOverrideReason(''); refreshInv(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const [proformaBusy, setProformaBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  // Generate the GST tax invoice in-app from this delivered order, then open its PDF.
  const generateInvoice = async () => {
    setGenBusy(true);
    try {
      const inv = await request<any>({ url: `/invoices/from-order/${id}`, method: 'POST', data: {} });
      message.success(`Invoice ${inv.invoiceNo} generated`);
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['order-invoices', id] });
      qc.invalidateQueries({ queryKey: ['order-timeline', id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e: any) {
      message.error(await readDownloadError(e, 'Failed to generate invoice'));
    } finally {
      setGenBusy(false);
    }
  };
  const downloadProforma = async () => {
    setProformaBusy(true);
    try {
      const r = await api.get(`/orders/${id}/proforma`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `proforma-${o?.orderNo ?? id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      message.error(await readDownloadError(e, 'Failed to download proforma'));
    } finally {
      setProformaBusy(false);
    }
  };

  if (isLoading) return <div style={{ padding: 24 }}><Spin tip="Loading…" /></div>;
  if (isError || !o) {
    return (
      <Result
        status="error"
        title="Couldn't load this order"
        subTitle="It may have been removed, or you may not have access."
        extra={<Button variant="primary" onClick={() => navigate('/orders')}>Back to orders</Button>}
      />
    );
  }

  // New-flow orders are billed before dispatch and go DELIVERED → CLOSED, so the
  // INVOICED stage never applies — drop it from the progress tracker.
  const gated = !!o.invoiceBeforeDispatch;
  const steps = gated ? STEPS.filter((s) => s !== 'INVOICED') : STEPS;
  const stepIdx = steps.indexOf(o.status);
  const totalPending = Object.values(pending ?? {}).reduce((s, v) => s + Number(v || 0), 0);
  // Summary "Discount" = the full reduction from subtotal down to the net taxable value — i.e. a line
  // discount AND a LOCKED advance cash discount (which nets the lines but is not stored in discount_amt).
  // Using subtotal − taxableAmt keeps the cards honest: Subtotal − Discount + Tax == Grand Total. For an
  // ESTIMATED CD the lines are still gross (taxableAmt == subtotal), so this is 0 until the CD is locked.
  const discountTotal = Number(o.taxableAmt) > 0
    ? Math.max(0, Number(o.subtotal ?? 0) - Number(o.taxableAmt ?? 0))
    : Number(o.discountAmt ?? 0);
  const discountPctLabel = Number(o.subtotal) > 0 && discountTotal > 0
    ? ` (${Math.round((discountTotal / Number(o.subtotal)) * 10000) / 100}%)` : '';
  // Effective ordered qty (respecting any short-close) — lets the invoice modal warn when
  // the order is only partially packed so users don't bill the whole order for a partial dispatch.
  const totalOrdered = (o.items ?? []).reduce((s: number, it: any) => s + Math.max(0, Number(it.qty ?? 0) - Number(it.cancelledQty ?? 0)), 0);

  // Invoice / payment readiness — drives both the "Close order" gate and the next-step
  // banner. The backend refuses to close an order while any non-cancelled invoice still
  // carries a balance, so we surface that up front instead of after a failed click.
  const liveInvoices = (invoices ?? []).filter((i: any) => i.status !== 'CANCELLED');
  const hasInvoice = liveInvoices.length > 0;
  // Per-shipment dispatch-invoice gate. dispatch() releases the PACKED shipment(s); in PER_SHIPMENT mode it
  // requires EACH to carry its OWN verified/overridden Tally invoice (shipment.service.dispatch). So an
  // order-level "some invoice is verified" check would wrongly enable Dispatch after only the FIRST shipment
  // was billed, leaving a newly packed, un-invoiced shipment the backend then rejects. Gate on the packed
  // shipments actually about to be released.
  const perShipment = o.invoiceMode === 'PER_SHIPMENT';
  const packedShipments = (shipments ?? []).filter((s: any) => s.status === 'PACKED');
  const shipInv = (s: any) => liveInvoices.filter((i: any) => String(i.shipmentId) === String(s.id));
  const packedNeedingInvoice = packedShipments.filter((s: any) => shipInv(s).length === 0);
  const packedInvoiceUnverified = packedShipments.filter((s: any) =>
    shipInv(s).length > 0 && !shipInv(s).some((i: any) => ['VERIFIED', 'OVERRIDDEN'].includes(i.verificationStatus)));
  // `gated` (defined above for the stepper) marks the new flow: the Tally tax invoice must be uploaded (at
  // PACKED) before dispatch. Legacy orders keep the old flow. PER_SHIPMENT → each packed shipment needs one.
  const needsInvoiceBeforeDispatch = gated && (perShipment ? packedNeedingInvoice.length > 0 : !hasInvoice);
  // Dispatch needs the invoice VERIFIED (or its mismatch admin-overridden) — matching the backend dispatch()
  // gate. Merely having an invoice on file is NOT enough. Legacy (non-gated) orders skip it.
  const dispatchInvoiceReady = !gated
    ? true
    : perShipment
      ? packedNeedingInvoice.length === 0 && packedInvoiceUnverified.length === 0
      : liveInvoices.some((i: any) => ['VERIFIED', 'OVERRIDDEN'].includes(i.verificationStatus));
  const invBilled = liveInvoices.reduce((s: number, i: any) => s + Number(i.grandTotal ?? 0), 0);
  const invPaid = liveInvoices.reduce((s: number, i: any) => s + Number(i.paidAmt ?? 0), 0);
  const unpaidInvoice = liveInvoices.find((i: any) => Number(i.balanceAmt ?? 0) > 0.01);
  // Order-based due (invoice allocation retired): the payment-summary drives close-readiness + the due
  // display, NOT the invoice balance (payments link to the order now, not the invoice). Falls back to
  // the legacy invoice balance only until the summary resolves.
  const paySummaryReady = paySummary !== undefined;
  const orderDue = paySummary ? Number(paySummary.due) : Number(unpaidInvoice?.balanceAmt ?? 0);
  const dueCleared = paySummary ? !!paySummary.dueCleared : (hasInvoice && !unpaidInvoice);
  const invoiceCleared = dueCleared;
  const invoicesReady = invoices !== undefined; // the /invoices query has resolved
  const canCloseNow = canEdit && paySummaryReady && dueCleared;
  // Cancel confirmation breakdown — how much of the order is packed / dispatched / delivered / pending, so
  // the person cancelling sees what's already gone (dispatched can't be recalled here) and, if partial, is
  // steered to short-closing just the remainder. Computed BEFORE the cancel gate below because whether
  // anything has shipped out is what decides a non-admin's eligibility.
  // Ignore CANCELLED shipments — their qty has already returned to pending (matches the backend's
  // pending/shipped math, which filters `s.status <> 'CANCELLED'`).
  const liveShipmentRows = (shipments ?? []).filter((s: any) => s.status !== 'CANCELLED');
  const shipQtyByStatus = (st: string) => liveShipmentRows
    .filter((s: any) => s.status === st)
    .reduce((sum: number, s: any) => sum + (s.items ?? []).reduce((a: number, it: any) => a + Number(it.qty || 0), 0), 0);
  const cancelBreakdown: CancelBreakdown = {
    ordered: totalOrdered,
    packed: shipQtyByStatus('PACKED'),
    dispatched: shipQtyByStatus('DISPATCHED'),
    delivered: shipQtyByStatus('DELIVERED'),
    pending: totalPending,
  };
  const hasShippedOut = cancelBreakdown.dispatched + cancelBreakdown.delivered > 1e-6;
  // Cancel: a retailer / sales person / sales head can withdraw an order only BEFORE anything leaves the
  // warehouse; an admin (ORDER.EDIT) can cancel anytime. During a partial dispatch the order still shows
  // PACKED even though earlier shipments are already DISPATCHED/DELIVERED — so gate on hasShippedOut, not
  // the order status alone, or the retailer sees a dead-end "Cancel" on goods that have already gone. The
  // un-shipped remainder is handled separately by short-close. (DELIVERED/INVOICED/CLOSED can't cancel anyway.)
  const canCancelNow = canCancel && (canEdit || (!['DISPATCHED', 'DELIVERED', 'INVOICED', 'CLOSED'].includes(o.status) && !hasShippedOut));
  // Full withdrawal is only possible while nothing has left the warehouse (a packed-only order auto-returns
  // its stock on cancel; a dispatched one is blocked by the backend) AND no live Tally invoice exists (the
  // backend refuses to cancel over a recorded invoice — it must be voided first). Short-close needs
  // ORDER.EDIT + the partial-fulfilment feature (PER_SHIPMENT orders) + something still pending.
  const canFullCancelNow = canCancelNow && !hasShippedOut && !hasInvoice;
  const fullCancelBlockedByInvoice = canCancelNow && !hasShippedOut && hasInvoice;
  // Short-close the un-shipped remainder. Allowed for RETAILER/SALES/Admin (ORDER.CANCEL, own orders via the
  // backend scope) when the feature is on and something is pending — EXCEPT when a whole-order invoice also
  // covers the pending (it must be voided / credit-noted first). Per-shipment orders bill each dispatch
  // separately, so the pending units are un-invoiced and drop cleanly (excess advance → advance-in-credit).
  const partialEnabled = !!(o as any).partialFulfillmentEnabled;
  // Mirror every backend cancel-remainder guard so the button only appears when it will actually succeed
  // (no dead-end server error): non-terminal status, no active advance CD, and no whole-order invoice.
  const shortCloseableStatus = !['DISPATCHED', 'DELIVERED', 'INVOICED', 'CLOSED', 'CANCELLED', 'REJECTED', 'DRAFT'].includes(o.status);
  const advanceCdActive = ['ESTIMATED', 'LOCKED'].includes(o.advanceCdStatus);
  const wholeInvoiceBlocksShortClose = totalPending > 1e-6 && hasInvoice && o.invoiceMode !== 'PER_SHIPMENT';
  const canShortCloseNow = canCancel && partialEnabled && totalPending > 1e-6 && shortCloseableStatus && !advanceCdActive && !wholeInvoiceBlocksShortClose;
  let shortCloseHint: { message: string; description: string } | undefined;
  if (totalPending > 1e-6 && !canShortCloseNow) {
    if (!partialEnabled) {
      shortCloseHint = { message: 'Short-close isn’t enabled here.', description: 'Partial cancellation is turned off in this environment.' };
    } else if (advanceCdActive) {
      // Differentiate: an ESTIMATED CD can be CLEARED (then short-close works); a LOCKED CD cannot be
      // cleared and the discount isn't recomputed on a partial yet — so be honest, not a false "clear it".
      shortCloseHint = o.advanceCdStatus === 'LOCKED'
        ? { message: 'This order’s advance cash discount is locked — the pending units can’t be short-closed here.', description: 'The discount was calculated on the full order; short-closing would change it, and recomputing a cash discount on a partial isn’t supported yet. Fulfil the pending units, or Accounts reconciles them.' }
        : { message: 'Clear the advance cash discount first.', description: 'This order has an advance/prepaid cash discount that isn’t locked yet — clear it in Accounts (the Advance CD panel), then the pending units can be short-closed.' };
    } else if (wholeInvoiceBlocksShortClose) {
      shortCloseHint = { message: 'Void the Tally invoice before short-closing.', description: 'This order is billed as a whole order (all units) — the pending units can’t be dropped until that invoice is voided or credit-noted in Billing (an admin handles this).' };
    } else if (!shortCloseableStatus) {
      shortCloseHint = { message: 'This order can’t be short-closed at its current stage.', description: 'Only an order still being fulfilled (not yet fully dispatched/closed) can have its pending units dropped.' };
    } else if (!canCancel) {
      shortCloseHint = { message: 'Ask an admin to short-close the pending units.', description: 'You don’t have permission to cancel part of this order.' };
    }
  }

  // Timeline tab side panel: who packed + how long each stage took (derived from history timestamps).
  const tl = timeline ?? [];
  const fulfilSteps = tl.length < 2 ? [] : tl.slice(1).map((t: any, i: number) => ({
    to: t.toStatus as string,
    ms: new Date(t.at).getTime() - new Date(tl[i].at).getTime(),
  }));
  const fulfilMaxMs = fulfilSteps.length ? Math.max(...fulfilSteps.map((s) => s.ms)) : 0;
  const fulfilTotalMs = tl.length > 1 ? new Date(tl[tl.length - 1].at).getTime() - new Date(tl[0].at).getTime() : 0;
  const packedByName = tl.find((t: any) => t.toStatus === 'PACKED')?.changedByName ?? null;

  // Plain-language "what to do next" for the current status. The INVOICED step is
  // payment-aware: it states whether dues are cleared and whether the order can close.
  const nextStep: { type: 'info' | 'success' | 'warning'; msg: string; desc: string; showInvoice?: boolean } | null = (() => {
    switch (o.status) {
      case 'PLACED': return { type: 'info', msg: 'Awaiting approval', desc: 'Review the order, then Approve to reserve stock — or Cancel.' };
      case 'AWAITING_PRODUCTION': return { type: 'warning', msg: 'Short on stock (legacy)', desc: 'Awaiting stock — resolve with a warehouse transfer or backorder, or cancel the order.' };
      case 'IN_PRODUCTION': return { type: 'warning', msg: 'Short on stock (legacy)', desc: 'Awaiting stock — resolve with a warehouse transfer or backorder, or cancel the order.' };
      case 'APPROVED': return { type: 'info', msg: 'Approved', desc: 'The assigned warehouse manager accepts it to begin fulfilment.' };
      case 'ACCEPTED': return { type: 'info', msg: 'Accepted', desc: 'Warehouse manager is preparing it — use “Pick & pack” to choose batches; that commits stock and packs the order.' };
      case 'PACKED': return needsInvoiceBeforeDispatch
        ? { type: 'warning', msg: 'Packed — invoice required before dispatch', desc: 'Accounts/Admin: bill this order in Tally (use the Proforma), then “Upload Tax Invoice”. Dispatch unlocks once the invoice is verified.' }
        : !dispatchInvoiceReady
          ? { type: 'warning', msg: 'Packed — verify the invoice before dispatch', desc: 'The Tally tax invoice is uploaded but not yet verified against the order. Verify it (Documents tab); a mismatch needs an admin override. Dispatch unlocks once it’s verified.' }
          : { type: 'info', msg: 'Packed', desc: 'Stock is committed. Release the goods with the “Dispatch” button.' };
      case 'DISPATCHED': return { type: 'info', msg: 'In transit', desc: 'Mark Delivered once the retailer receives it (attach POD if available).' };
      case 'DELIVERED': return gated
        ? { type: 'info', msg: 'Delivered', desc: 'The tax invoice was raised before dispatch. Record payment, then Close the order once dues are cleared.' }
        : { type: 'info', msg: 'Delivered', desc: 'Generate the GST tax invoice for this order.' };
      case 'INVOICED':
        if (!hasInvoice) return { type: 'info', msg: 'Invoiced', desc: 'Close the order once payment is settled.' };
        return dueCleared
          ? { type: 'success', msg: 'Paid in full — ready to close', desc: 'All dues on this order are cleared. You can now Close it.' }
          : { type: 'warning', msg: 'Payment pending — order cannot be closed yet', desc: `${inr2(orderDue)} still outstanding on this order. Record/approve the payment, then Close.`, showInvoice: false };
      case 'CLOSED': return { type: 'success', msg: 'Order closed', desc: 'This order is complete. Use Reorder to place the same items again.' };
      default: return null; // DRAFT, CANCELLED, AWAITING_TRANSFER (which has its own banner)
    }
  })();

  return (
    <div>
      {/* Header */}
      <Card style={{ marginBottom: 12 }}>
        <Row align="middle" gutter={[16, 16]}>
          <Col flex="auto">
            <Typography.Title level={4} style={{ margin: 0 }}>
              {o.orderNo} <Tag color={STATUS_COLOR[o.status]} style={{ marginLeft: 8 }}>{statusLabel(o.status)}</Tag>
            </Typography.Title>
            <Typography.Text type="secondary">
              {(o.retailerName || nameOf(retailerName, o.retailerId))} · placed {formatIst(o.orderDate)} · channel <b>{o.channel}</b>
            </Typography.Text>
          </Col>
          <Col>
            <Space wrap>
              {/* Reorder is a retailer/sales action — hide it entirely for roles without ORDER.CREATE
                  (e.g. Warehouse Manager, Accounts) rather than showing a disabled button. */}
              {canCreate && ['DELIVERED', 'INVOICED', 'CLOSED'].includes(o.status) && (
                <Tooltip label="Place a new order with the same items">
                  <Popconfirm
                    title="Place a reorder?"
                    description="Copies all items into a fresh draft. Prices and taxes are recalculated."
                    onConfirm={() => reorder.mutate()}
                  >
                    <Button icon={<Repeat size={16} />} loading={reorder.isPending}>
                      Reorder
                    </Button>
                  </Popconfirm>
                </Tooltip>
              )}
              {/* New flow: the Tally tax invoice must be uploaded (while PACKED) before
                  dispatch. Shown to invoice creators until an invoice is on file — also
                  reappears at DISPATCHED/DELIVERED if the invoice was voided (correction). */}
              {gated && o.invoiceMode !== 'PER_SHIPMENT' && !hasInvoice && ['PACKED', 'DISPATCHED', 'DELIVERED'].includes(o.status) && canUploadInvoice && (
                <GatedButton
                  allowed={canUploadInvoice}
                  reason="You don't have permission to upload the Tally invoice (requires INVOICE.CREATE or warehouse pack rights)"
                  variant="primary"
                  icon={<Upload size={16} />}
                  onClick={() => setInvoiceUploadOpen(true)}
                >
                  Upload Tax Invoice
                </GatedButton>
              )}
              {/* Per-shipment header shortcut: the upload also lives in the panel below, but surfacing it in
                  the action bar shows how to unblock a greyed-out Dispatch. Auto-targets the one packed
                  shipment awaiting its invoice; a picker handles the rare 2+ case. Reuses the SAME modal. */}
              {o.invoiceMode === 'PER_SHIPMENT' && shipmentsNeedingInvoice.length > 0 && canUploadInvoice && (
                <GatedButton
                  allowed={canUploadInvoice}
                  reason="You don't have permission to upload the Tally invoice (requires INVOICE.CREATE or warehouse pack rights)"
                  variant="primary"
                  icon={<Upload size={16} />}
                  onClick={() => (shipmentsNeedingInvoice.length === 1 ? setInvoiceForShipment(shipmentsNeedingInvoice[0]) : setInvoicePickerOpen(true))}
                >
                  Upload invoice
                </GatedButton>
              )}
              {/* Pick & pack: choose source batches per line → creates the packed shipment and
                  commits stock. Available while anything is still pending to pack. */}
              {['ACCEPTED', 'PACKED'].includes(o.status) && totalPending > 1e-6 && canPack && (
                <GatedButton
                  allowed={canPack}
                  reason="You don't have permission to pack this order (requires ORDER.EDIT or warehouse pack rights)"
                  variant="primary"
                  icon={<Inbox size={16} />}
                  onClick={() => setDispatchOpen(true)}
                >
                  {o.status === 'PACKED' ? 'Pack more' : 'Pick & pack'}
                </GatedButton>
              )}
              {/* Dispatch (release): flips the packed shipment(s) to DISPATCHED — the goods leave.
                  No stock movement (committed at pack). New-flow orders stay blocked until the
                  verified Tally invoice is on file. */}
              {hasPackedShipment && canPack && (() => {
                // A "partial" dispatch = un-shipped qty still remains, so this only releases the packed
                // part and the order stays PACKED. Label + confirm say so (and give the split); a final
                // dispatch (nothing left pending) reads plain "Dispatch".
                const partial = totalPending > 1e-6;
                const shipping = cancelBreakdown.packed; // qty in the packed shipment(s) this release sends
                return (
                  <Popconfirm
                    title={partial ? 'Partial dispatch?' : 'Dispatch this order?'}
                    description={partial
                      ? `You're dispatching ${fmtQty(shipping)} now — ${fmtQty(totalPending)} will stay pending. The packed goods leave the warehouse (stock was committed at pack).`
                      : 'The packed goods leave the warehouse. Stock was already committed at pack.'}
                    okText={partial ? 'Partial dispatch' : 'Dispatch'}
                    disabled={!(canPack && dispatchInvoiceReady)}
                    onConfirm={() => dispatch.mutate()}
                  >
                    <GatedButton
                      allowed={canPack && dispatchInvoiceReady}
                      reason={!canPack
                        ? "You don't have permission to dispatch this order (requires ORDER.EDIT or warehouse pack rights)"
                        : needsInvoiceBeforeDispatch
                          ? (perShipment
                              ? `Upload the Tally tax invoice for shipment ${packedNeedingInvoice.map((s: any) => s.shipmentNo).join(', ')} before dispatch.`
                              : 'Upload the Tally tax invoice PDF first — it’s required before dispatch.')
                          : (perShipment
                              ? `Verify the Tally tax invoice for shipment ${packedInvoiceUnverified.map((s: any) => s.shipmentNo).join(', ')} (Documents tab) before dispatch.`
                              : 'Verify the Tally tax invoice (Documents tab) before dispatch — a mismatch needs an admin override.')}
                      variant="primary"
                      icon={<Send size={16} />}
                      loading={dispatch.isPending}
                    >
                      {partial ? 'Partial dispatch' : 'Dispatch'}
                    </GatedButton>
                  </Popconfirm>
                );
              })()}
              {/* Delivery is recorded per shipment (with optional POD) — a STAFF/field action (ORDER.DELIVER:
                  Sales/Head/Admin). Gated on canDeliver so a retailer never even sees it (no greyed button).
                  This header shortcut delivers the open shipment, or jumps to the Documents tab when there
                  is more than one. Shown whenever a dispatched shipment is awaiting delivery — including a
                  partial order still PACKED (30 shipped, 20 pending), not only a fully DISPATCHED order. */}
              {canDeliver && ['PACKED', 'DISPATCHED'].includes(o.status) && openShipments.length > 0 && (
                <GatedButton
                  allowed={canDeliver}
                  reason="You don't have permission to mark deliveries (requires ORDER.DELIVER)"
                  variant="primary"
                  icon={<Flag size={16} />}
                  onClick={handleMarkDelivered}
                >
                  Mark Delivered
                </GatedButton>
              )}
              {/* Held for stock consolidation — admin can cancel (in-transit stock returns to source). */}
              {o.status === 'AWAITING_TRANSFER' && (
                <GatedButton
                  allowed={canEdit}
                  reason="You don't have permission to manage this order (requires ORDER.EDIT)"
                  danger
                  icon={<ArrowLeftRight size={16} />}
                  loading={cancelConsolidation.isPending}
                  onClick={() => cancelConsolidation.mutate()}
                >
                  Cancel consolidation
                </GatedButton>
              )}
              {(NEXT[o.status] ?? [])
                // AWAITING_PRODUCTION / IN_PRODUCTION, once production refills stock: re-approve
                // only if it was never approved; if it was already approved, pack directly. While
                // still short, neither Approve nor Pack shows (it self-heals when stock lands).
                .filter((s) => !((o.status === 'AWAITING_PRODUCTION' || o.status === 'IN_PRODUCTION') && s === 'APPROVED' && (o.stockComplete === false || o.everApproved)))
                .filter((s) => !((o.status === 'AWAITING_PRODUCTION' || o.status === 'IN_PRODUCTION') && s === 'PACKED' && (o.stockComplete === false || !o.everApproved)))
                // Dispatch is done via the shipment modal (it allocates stock),
                // not a bare status flip — so hide the raw DISPATCHED button.
                .filter((s) => !(o.status === 'PACKED' && s === 'DISPATCHED'))
                // Delivery is recorded per shipment (Documents tab, with POD);
                // the order auto-completes — so hide the order-level Delivered flip.
                .filter((s) => !(o.status === 'DISPATCHED' && s === 'DELIVERED'))
                // Invoicing is done via "Generate Invoice" below, not a flip.
                .filter((s) => !(o.status === 'DELIVERED' && s === 'INVOICED'))
                // New-flow orders are already invoiced before dispatch, so they can close
                // straight from DELIVERED. Legacy orders must invoice first — hide it there.
                .filter((s) => !(o.status === 'DELIVERED' && s === 'CLOSED' && !gated))
                .map((s) =>
                  s === 'CANCELLED' ? (
                    // Opens a breakdown-aware confirmation: full withdrawal when nothing has shipped, or
                    // short-close of just the pending remainder once goods are out (dispatched can't be cancelled).
                    <GatedButton
                      key={s}
                      allowed={canCancelNow || canShortCloseNow}
                      reason={!canCancel
                        ? "You don't have permission to cancel this order"
                        : 'This order is already dispatched — only an admin can cancel it now'}
                      danger
                      icon={STATUS_ICON[s]}
                      onClick={() => setCancelModalOpen(true)}
                    >
                      {ACTION_LABEL[s] ?? s}
                    </GatedButton>
                  ) : (
                    <GatedButton
                      key={s}
                      allowed={s === 'CLOSED' ? canCloseNow : (s === 'PACKED' ? canPack : (s === 'ACCEPTED' ? canAccept : canChangeStatus))}
                      reason={
                        s === 'CLOSED'
                          ? (!canEdit
                              ? 'Only Admin can close an order (requires ORDER.EDIT) — a Sales Head can approve only.'
                              : !paySummaryReady
                                ? 'Checking payment status…'
                                : `Clear the ${inr2(orderDue)} still outstanding on this order first.`)
                          : s === 'PACKED'
                            ? 'Packing requires ORDER.EDIT or warehouse pack rights (Admin or the assigned Warehouse Manager).'
                            : s === 'ACCEPTED'
                              ? 'Accepting requires warehouse pack rights (the assigned Warehouse Manager or an Admin).'
                              : "You don't have permission to change this order's status (requires ORDER.APPROVE or ORDER.EDIT)"
                      }
                      variant="primary"
                      icon={STATUS_ICON[s]}
                      onClick={() => (s === 'APPROVED' ? setApproveOpen(true) : s === 'PACKED' ? setDispatchOpen(true) : change.mutate(s))}
                      loading={change.isPending && change.variables === s}
                    >
                      {ACTION_LABEL[s] ?? s}
                    </GatedButton>
                  ),
                )}
              {/* Legacy flow only: in-app invoice generation after delivery. New-flow
                  orders are already invoiced (uploaded at PACKED), so this is hidden. */}
              {o.status === 'DELIVERED' && !gated && !hasInvoice && (
                <GatedButton
                  allowed={canInvoice}
                  reason="You don't have permission to create invoices (requires INVOICE.CREATE)"
                  variant="primary"
                  icon={<FileCheck size={16} />}
                  loading={genBusy}
                  onClick={generateInvoice}
                >
                  Generate Invoice
                </GatedButton>
              )}
              {canPay && paySummary && Number(paySummary.due) > 0.01 && !['DRAFT', 'CANCELLED', 'REJECTED', 'CLOSED'].includes(o.status) && (
                <Button variant="primary" icon={<Wallet size={16} />} onClick={() => setPayOpen(true)}>
                  Pay {inr2(paySummary.due)}
                </Button>
              )}
              {canProforma && !['DRAFT', 'CANCELLED', 'REJECTED'].includes(o.status) && (
                <Button icon={<FileText size={16} />} onClick={downloadProforma} loading={proformaBusy}>
                  Proforma
                </Button>
              )}
              {/* Raise a return — only once goods have actually shipped out and a tax invoice exists (a return
                  reverses an invoice). A retailer/sales action: hidden entirely for roles without RETURN.CREATE
                  (Warehouse Manager, Accounts) rather than shown disabled. */}
              {RETURNS_LIFECYCLE_ENABLED && canRaiseReturn && hasInvoice && hasShippedOut && !['DRAFT', 'CANCELLED', 'REJECTED'].includes(o.status) && (
                <Button
                  variant="default"
                  icon={<Undo2 size={16} />}
                  onClick={() => setReturnOpen(true)}
                >
                  Raise return
                </Button>
              )}
            </Space>
          </Col>
        </Row>

        {o.status === 'AWAITING_TRANSFER' && (
          <Alert type="warning" showIcon style={{ marginTop: 12 }}
            message="Consolidating stock across warehouses"
            description={
              <div>
                <div style={{ marginBottom: 6 }}>
                  Held until the inter-warehouse transfers are received. The order returns to its previous
                  state automatically once they land — then dispatch it as a single shipment.
                </div>
                {((o as any).transfers ?? []).map((t: any) => (
                  <div key={t.id} className="text-sm">
                    <b>{t.transferNo}</b> · {t.totalQty} units{' '}
                    <Tag color={t.status === 'RECEIVED' ? 'green' : t.status === 'IN_TRANSIT' ? 'gold' : 'default'}>{t.status}</Tag>
                  </div>
                ))}
              </div>
            } />
        )}

        {nextStep && (
          <Alert
            type={nextStep.type}
            showIcon
            style={{ marginTop: 12 }}
            message={nextStep.msg}
            description={nextStep.desc}
            action={nextStep.showInvoice && unpaidInvoice && hasPerm('INVOICE.VIEW') ? (
              <Button size="sm" onClick={() => navigate(`/billing/invoices/${unpaidInvoice.id}`)}>View invoice</Button>
            ) : undefined}
          />
        )}

        {stepIdx >= 0 && o.status !== 'CANCELLED' && (
          <>
            <Divider style={{ margin: '12px 0' }} />
            <Steps
              size="small"
              current={stepIdx}
              items={steps.map((s) => ({
                title: s,
                icon: STATUS_ICON[s],
                // New flow raises the invoice at PACKED — flag it so the tracker shows
                // where billing happened (instead of a now-removed INVOICED step).
                ...(gated && s === 'PACKED' && hasInvoice ? { description: 'Billed ✓' } : {}),
              }))}
            />
          </>
        )}
      </Card>

      {/* Advance / prepaid cash discount — before-billing CD state + the Accounts/Admin/SA lock action. */}
      {canSeeMoney && <AdvanceCdPanel orderId={id} orderStatus={o.status} canLock={canLockCd} canChoose={canPay} />}

      {/* Admin stock planning: pick the dispatch warehouse, see warehouse/batch availability + FEFO.
          Gated by ORDER.DISPATCH.PLAN (or ORDER.EDIT) — matching the backend — and the fulfilment window.
          Once an order is fully packed the allocation is done (shown in Items → Allocated batches), so the
          planning aid is hidden; it stays only for a PACKED order with a remaining tail to pack. */}
      {(FULFILLMENT_V2_ENABLED
        // V2 window also covers ACCEPTED — that's when the WHM is preparing to pack and may find the
        // assigned warehouse short (the exact case coverage/transfer-suggestion is for). Assign + Consolidate
        // both already allow ACCEPTED, so the actions stay consistent.
        ? (['APPROVED', 'ACCEPTED', 'BACKORDER', 'AWAITING_TRANSFER'].includes(o.status) || (o.status === 'PACKED' && totalPending > 1e-6))
        : (['APPROVED', 'BACKORDER', 'AWAITING_TRANSFER'].includes(o.status) || (o.status === 'PACKED' && totalPending > 1e-6))) && (
        FULFILLMENT_V2_ENABLED
          // V2: a read-only coverage matrix visible to planners AND view-only approvers (actions gated by canPlan).
          ? ((canPlan || canChangeStatus || canReportShortfall) && (
            <WarehouseCoveragePanel orderId={id} dispatchWarehouseId={o.dispatchWarehouseId} canPlan={canPlan} canAddStock={canAddStock}
              canReportShortfall={canReportShortfall} orderStatus={o.status} transfers={(o as any).transfers} />
          ))
          // Legacy: single-warehouse planning aid, planners only (unchanged behaviour when the flag is off).
          : (canPlan && (
            <StockPlanPanel orderId={id} dispatchWarehouseId={o.dispatchWarehouseId} canEdit={canPlan} />
          ))
      )}

      {/* Per-shipment (partial) invoicing: each dispatch is invoiced separately. Renders ONLY for
          PER_SHIPMENT orders — WHOLE orders keep the order-level "Upload Tax Invoice" button above. */}
      {o.invoiceMode === 'PER_SHIPMENT' && (shipments ?? []).filter((s: any) => s.status !== 'CANCELLED').length > 0 && (
        <Card title="Per-shipment tax invoices" style={{ marginBottom: 12 }}>
          <div className="text-sm" style={{ color: '#8c8c8c', marginBottom: 8 }}>
            This order is invoiced per shipment. Upload the Tally invoice for a packed shipment (billing only that shipment) — its dispatch unlocks once the invoice is verified.
          </div>
          {(shipments ?? []).filter((s: any) => s.status !== 'CANCELLED').map((s: any) => {
            const inv = (invoices ?? []).find((i: any) => String(i.shipmentId) === String(s.id) && i.status !== 'CANCELLED');
            const vs = inv?.verificationStatus;
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderTop: '1px solid #f5f5f5' }}>
                <div className="text-sm">
                  <b>{s.shipmentNo}</b> <Tag>{s.status}</Tag>
                  {inv
                    ? <span> · {inv.invoiceNo} · {inr2(inv.grandTotal)} <Tag color={vs === 'VERIFIED' || vs === 'OVERRIDDEN' ? 'green' : vs === 'MISMATCH' ? 'red' : 'gold'}>{vs}</Tag></span>
                    : <span style={{ color: '#d46b08' }}> · no invoice yet</span>}
                </div>
                {!inv && canUploadInvoice && ['PACKED', 'DISPATCHED', 'DELIVERED'].includes(s.status) && (
                  <Button size="sm" variant="primary" icon={<Upload size={16} />} onClick={() => setInvoiceForShipment(s)}>
                    Upload invoice
                  </Button>
                )}
              </div>
            );
          })}
        </Card>
      )}

      {canSeeMoney && (
      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Subtotal" value={Number(o.subtotal ?? 0)} formatter={(v) => inr2(v)} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title={`Discount${discountPctLabel}`} value={discountTotal} formatter={(v) => inr2(v)} valueStyle={{ color: '#cf1322' }} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Tax" value={Number(o.cgstAmt ?? 0) + Number(o.sgstAmt ?? 0) + Number(o.igstAmt ?? 0)} formatter={(v) => inr2(v)} /></Card></Col>
        <Col xs={12} md={6}>
          <Card>
            {o.advanceCdStatus === 'ESTIMATED' ? (
              <>
                <Statistic title="Net Payable (advance CD)" value={Number(o.netGrandTotal ?? o.grandTotal)} formatter={(v) => inr2(v)} valueStyle={{ color: brand.primary }} />
                <div className="text-xs" style={{ marginTop: 2, color: '#8c8c8c' }}>
                  <span style={{ textDecoration: 'line-through' }}>{inr2(o.grandTotal)}</span> original · discount pending lock
                </div>
              </>
            ) : (
              <Statistic title="Grand Total" value={Number(o.grandTotal ?? 0)} formatter={(v) => inr2(v)} valueStyle={{ color: brand.primary }} />
            )}
          </Card>
        </Col>
      </Row>
      )}

      {/* Hide the settle-advance banner in the two states the backend hard-rejects it (active advance CD →
          deferred CD-settlement step; CANCELLED/REJECTED → use the payment Refund action instead) — mirrors
          the short-close gating above so the action never dead-ends. */}
      {canSeeMoney && Number(advanceExcess?.excess ?? 0) > 0.01 && !advanceCdActive && !['CANCELLED', 'REJECTED'].includes(o.status) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={<span>Advance‑in‑credit <b>{inr2(advanceExcess.excess)}</b> on this order — the retailer paid more than it was finally billed.</span>}
          description={`Paid ${inr2(advanceExcess.paidNet)} · billed ${inr2(advanceExcess.billed)}. Carried as their credit for the next order — refund it here if they want cash back.`}
          action={canRefund && (o as any).partialFulfillmentEnabled
            ? <Button size="sm" variant="primary" onClick={() => setSettleOpen(true)}>Settle advance</Button>
            : undefined}
        />
      )}

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <Descriptions bordered column={3} size="small">
                  <Descriptions.Item label="Status"><Tag color={STATUS_COLOR[o.status]}>{statusLabel(o.status)}</Tag></Descriptions.Item>
                  <Descriptions.Item label="Channel">{o.channel}</Descriptions.Item>
                  <Descriptions.Item label="Date">{formatIst(o.orderDate)}</Descriptions.Item>
                  <Descriptions.Item label="Placed by">{o.placedByName || nameOf(userName, o.createdBy)}</Descriptions.Item>
                  <Descriptions.Item label="Retailer">{(o.retailerName || nameOf(retailerName, o.retailerId))}</Descriptions.Item>
                  <Descriptions.Item label="Phone">{o.retailerMobile ? <a href={`tel:${o.retailerMobile}`}>{o.retailerMobile}</a> : '—'}</Descriptions.Item>
                  <Descriptions.Item label="Sales person">{o.salesPersonName || nameOf(userName, o.salesPersonId)}</Descriptions.Item>
                  <Descriptions.Item label="Warehouse">
                    {o.dispatchWarehouseId
                      ? (o.dispatchWarehouseName || nameOf(warehouseName, o.dispatchWarehouseId))
                      : <span style={{ color: '#999' }}>Assigned at packing</span>}
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={3}>
                    {o.retailerAddress ? (
                      <Space size="middle" wrap>
                        <span><MapPin size={16} /> {o.retailerAddress}</span>
                        <a
                          href={o.retailerLat && o.retailerLng
                            ? `https://www.google.com/maps?q=${o.retailerLat},${o.retailerLng}`
                            : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.retailerAddress)}`}
                          target="_blank"
                          rel="noopener"
                        >
                          View map
                        </a>
                      </Space>
                    ) : '—'}
                  </Descriptions.Item>
                  {canSeeMoney && <Descriptions.Item label="Subtotal">{inr2(o.subtotal)}</Descriptions.Item>}
                  {canSeeMoney && <Descriptions.Item label={`Discount${discountPctLabel}`}>{inr2(discountTotal)}</Descriptions.Item>}
                  {canSeeMoney && <Descriptions.Item label="Taxable">{inr2(o.taxableAmt)}</Descriptions.Item>}
                  {canSeeMoney && <Descriptions.Item label="CGST">{inr2(o.cgstAmt)}</Descriptions.Item>}
                  {canSeeMoney && <Descriptions.Item label="SGST">{inr2(o.sgstAmt)}</Descriptions.Item>}
                  {canSeeMoney && <Descriptions.Item label="IGST">{inr2(o.igstAmt)}</Descriptions.Item>}
                  {canSeeMoney && Math.abs(Number(o.roundOff ?? 0)) >= 0.005 && (
                    <Descriptions.Item label="Round off" span={3}>{Number(o.roundOff) < 0 ? '−' : '+'}{inr2(Math.abs(Number(o.roundOff)))}</Descriptions.Item>
                  )}
                  {canSeeMoney && (
                  <Descriptions.Item label="Grand Total" span={3}>
                    <b>{inr2(o.grandTotal)}</b>
                    {o.advanceCdStatus === 'ESTIMATED' && (
                      <span className="text-xs" style={{ marginLeft: 8, color: '#d46b08' }}>
                        → Net payable {inr2(o.netGrandTotal)} after advance cash discount (estimated · pending lock)
                      </span>
                    )}
                  </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Remarks" span={3}>{o.remarks ?? '—'}</Descriptions.Item>
                  {o.approvedAt && (
                    <Descriptions.Item label="Approved" span={3}>
                      by {o.approvedByName || nameOf(userName, o.approvedBy)} on {formatIst(o.approvedAt)}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
            {
              key: 'items',
              label: `Items (${o.items?.length ?? 0})`,
              children: (
                <Table
                  rowKey="id"
                  dataSource={o.items ?? []}
                  pagination={false}
                  size="small"
                  scroll={{ x: 1000 }}
                  summary={(items) => {
                    const arr = items as any[];
                    const qty = arr.reduce((s, r) => s + Number(r.qty ?? 0), 0);
                    const total = arr.reduce((s, r) => s + Number(r.lineTotal ?? 0), 0);
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}><b>Total</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={1}><b>{fmtQty(qty)}</b></Table.Summary.Cell>
                        {canSeeMoney && <Table.Summary.Cell index={2} colSpan={6} />}
                        {canSeeMoney && <Table.Summary.Cell index={3}><b>{inr2(total)}</b></Table.Summary.Cell>}
                      </Table.Summary.Row>
                    );
                  }}
                  columns={[
                    { title: 'Product', dataIndex: 'productNameSnapshot', width: 320, render: (v: any, r: any) => {
                      const allocs = batchesByLine.get(String(r.id)) ?? [];
                      return (
                        <div>
                          <div style={{ fontWeight: 500 }}>{v}{r.variantLabel ? ` ${r.variantLabel}` : ''}</div>
                          {allocs.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              <div className="text-2xs" style={{ color: '#8c8c8c', marginBottom: 2 }}>Allocated batches</div>
                              {allocs.map((a, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.9 }}>
                                  <span className="text-xs" style={{ fontFamily: 'monospace' }}>{a.batchNo}</span>
                                  <Tag color="blue" style={{ margin: 0 }}>×{fmtQty(a.qty)}</Tag>
                                  {a.expiry && <span className="text-2xs" style={{ color: '#8c8c8c', whiteSpace: 'nowrap' }}>exp {fmtDateShort(a.expiry)}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    } },
                    { title: 'HSN', dataIndex: 'hsnCode', width: 90 },
                    { title: 'Qty', dataIndex: 'qty', align: 'right' as const, width: 150, render: (_: any, r: any) => {
                      const phrase = boxLoosePhrase(r.boxQuantity, r.looseUnitQuantity, r.unitsPerBoxSnapshot);
                      if (phrase) {
                        return <span><b>{phrase}</b><div className="text-2xs" style={{ color: '#8c8c8c' }}>= {fmtQty(r.qty)} units</div></span>;
                      }
                      return fmtQty(Number(r.qty));
                    } },
                    ...(canSeeMoney ? [
                    { title: 'Rate', dataIndex: 'unitPrice', align: 'right' as const, render: inr, width: 90 },
                    { title: 'Disc %', dataIndex: 'discountPct', align: 'right' as const, width: 80 },
                    { title: 'Taxable', dataIndex: 'taxableAmt', align: 'right' as const, render: inr },
                    { title: 'CGST', dataIndex: 'cgstAmt', align: 'right' as const, render: inr },
                    { title: 'SGST', dataIndex: 'sgstAmt', align: 'right' as const, render: inr },
                    { title: 'IGST', dataIndex: 'igstAmt', align: 'right' as const, render: inr },
                    { title: 'Line Total', dataIndex: 'lineTotal', align: 'right' as const, render: (v: any) => <b>{inr2(v)}</b> },
                    ] : []),
                  ]}
                />
              ),
            },
            {
              key: 'timeline',
              label: 'Timeline',
              children: !lifecycleEvents.length ? (
                <Empty description="No activity yet" />
              ) : (
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={9}>
                    <Typography.Text strong>At a glance</Typography.Text>
                    <Descriptions column={1} size="small" colon style={{ marginTop: 8 }}>
                      <Descriptions.Item label="Retailer">{(o.retailerName || nameOf(retailerName, o.retailerId))}</Descriptions.Item>
                      <Descriptions.Item label="Sales person">{o.salesPersonName || nameOf(userName, o.salesPersonId)}</Descriptions.Item>
                      <Descriptions.Item label="Approved by">{o.approvedBy ? (o.approvedByName || nameOf(userName, o.approvedBy)) : '—'}</Descriptions.Item>
                      <Descriptions.Item label="Warehouse">{o.dispatchWarehouseId ? (o.dispatchWarehouseName || nameOf(warehouseName, o.dispatchWarehouseId)) : '—'}</Descriptions.Item>
                      <Descriptions.Item label="Packed by">{packedByName ?? '—'}</Descriptions.Item>
                      <Descriptions.Item label="Payment">{!dueCleared && orderDue > 0.01 ? <Tag color="red" style={{ margin: 0 }}>{inr2(orderDue)} due</Tag> : <Tag color="green" style={{ margin: 0 }}>Paid</Tag>}</Descriptions.Item>
                      <Descriptions.Item label="Invoice">{liveInvoices.length ? <Space size={4}>{liveInvoices[0].invoiceNo}{liveInvoices[0].verificationStatus && <Tag style={{ margin: 0 }}>{liveInvoices[0].verificationStatus}</Tag>}</Space> : '—'}</Descriptions.Item>
                    </Descriptions>
                    {fulfilSteps.length > 0 && (
                      <>
                        <Divider style={{ margin: '12px 0 8px' }} />
                        <Typography.Text strong>Fulfilment timing</Typography.Text>
                        <div style={{ marginTop: 8 }}>
                          {fulfilSteps.map((s, i) => (
                            <div key={i} className="text-sm" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '3px 0' }}>
                              <span style={{ color: token.colorTextSecondary }}>
                                {statusLabel(s.to)}
                                {s.ms === fulfilMaxMs && fulfilMaxMs > 0 && <Tag color="orange" style={{ marginLeft: 6 }}>slowest</Tag>}
                              </span>
                              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDuration(s.ms)}</span>
                            </div>
                          ))}
                          <Divider style={{ margin: '8px 0' }} />
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                            <span>Total turnaround</span>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtDuration(fulfilTotalMs)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </Col>
                  <Col xs={24} lg={15}>
                    <Timeline
                      items={lifecycleEvents.map((e) => ({
                        color: e.color,
                        dot: e.icon,
                        children: (
                          // Single left-aligned column: what happened (+ time), then who, then notes/proof.
                          <div style={{ paddingBottom: 4 }}>
                            <Space size={6} wrap style={{ marginBottom: 2 }}>
                              <span>{e.title}</span>
                              <span className="text-xs" style={{ color: token.colorTextTertiary }}>{formatIst(e.at)}</span>
                            </Space>
                            {e.who && (
                              <div className="text-xs" style={{ color: token.colorTextSecondary }}>
                                <User size={16} /> {e.who}
                              </div>
                            )}
                            {e.note && (
                              <div className="text-xs" style={{ color: e.noteStrong ? token.colorText : token.colorTextSecondary, fontWeight: e.noteStrong ? 600 : undefined, marginTop: 2 }}>
                                <i>{e.note}</i>
                              </div>
                            )}
                            {e.proof && (
                              <div className="text-xs" style={{ marginTop: 2 }}>
                                <a href={e.proof} target="_blank" rel="noopener">view proof</a>
                              </div>
                            )}
                          </div>
                        ),
                      }))}
                    />
                  </Col>
                </Row>
              ),
            },
            ...(canSeeMoney ? [{
              key: 'payments',
              label: `Payments (${orderPayments?.length ?? 0})`,
              children: !orderPayments?.length ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payments submitted for this order yet." />
              ) : (
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={orderPayments}
                  pagination={false}
                  columns={[
                    { title: 'Payment No', dataIndex: 'paymentNo' },
                    { title: 'Date', dataIndex: 'paymentDate', render: (v: any) => formatIst(v) },
                    { title: 'Amount', dataIndex: 'amount', align: 'right' as const, render: (v: any) => <b>{inr2(v)}</b> },
                    { title: 'Mode', dataIndex: 'mode', render: (v: string, p: any) => p.fromCredit ? <Tag color="green">Account credit</Tag> : <Tag>{v}</Tag> },
                    {
                      title: 'Status', dataIndex: 'approvalStatus',
                      render: (s: string, p: any) => {
                        if (p.status === 'REFUNDED') {
                          return (
                            <Tooltip label={`Refunded ${inr2(p.refundAmount ?? p.amount)} via ${p.refundMode ?? ''}${p.refundedByName ? ` by ${p.refundedByName}` : ''}${p.refundReason ? ` — ${p.refundReason}` : ''}`}>
                              <Tag color="purple">Refunded</Tag>
                            </Tooltip>
                          );
                        }
                        const color = s === 'APPROVED' ? 'green' : s === 'REJECTED' ? 'red' : 'gold';
                        const label = s === 'PENDING' ? 'Pending approval' : s === 'APPROVED' ? 'Approved' : 'Rejected';
                        return s === 'REJECTED' && p.rejectedReason
                          ? <Tooltip label={`Reason: ${p.rejectedReason}`}><Tag color={color}>{label}</Tag></Tooltip>
                          : <Tag color={color}>{label}</Tag>;
                      },
                    },
                    {
                      title: 'Proof', key: 'proof', align: 'center' as const,
                      render: (_: any, p: any) => {
                        const files: string[] = (p.attachmentUrls?.length ? p.attachmentUrls : (p.attachmentUrl ? [p.attachmentUrl] : []));
                        return (
                          <Space size={8}>
                            {files.length ? <a href={files[0]} target="_blank" rel="noopener">payment</a> : '—'}
                            {p.status === 'REFUNDED' && p.refundProofUrls?.[0] && <a href={p.refundProofUrls[0]} target="_blank" rel="noopener">refund</a>}
                          </Space>
                        );
                      },
                    },
                    ...(canRefund ? [{
                      title: '', key: 'refundAct', align: 'right' as const, width: 160,
                      render: (_: any, p: any) => {
                        if (p.status === 'REFUNDED') return null; // shown as "Refunded" in Status
                        // An advance is refundable once its order is cancelled/rejected (and it wasn't itself rejected).
                        const orderResolved = ['CANCELLED', 'REJECTED'].includes(o.status);
                        if (orderResolved && p.approvalStatus !== 'REJECTED') {
                          return <Button size="sm" danger icon={<Repeat size={16} />} onClick={() => setRefundFor(p)}>Process refund</Button>;
                        }
                        return null;
                      },
                    }] : []),
                  ]}
                />
              ),
            }] : []),
            {
              key: 'docs',
              label: `Documents (${(shipments?.length ?? 0) + (invoices?.length ?? 0)})`,
              children: (
                <>
                  <Typography.Title level={5}>Shipments ({shipments?.length ?? 0})</Typography.Title>
                  {!shipments?.length ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No shipments yet — use Pick & pack above to create one." />
                  ) : (
                    <Table
                      rowKey="id"
                      size="small"
                      dataSource={shipments}
                      pagination={false}
                      expandable={{
                        expandedRowRender: (s: any) => (
                          <Table
                            size="small"
                            rowKey="id"
                            dataSource={s.items ?? []}
                            pagination={false}
                            columns={[
                              { title: 'Product', dataIndex: 'productId', render: (v: any) => productName.get(String(v)) ?? `#${v}` },
                              { title: 'Batch', dataIndex: 'batchNo', render: (v: any, r: any) => v
                                ? <><span style={{ fontFamily: 'monospace' }}>{v}</span>{r.expiry ? <span style={{ color: '#8c8c8c' }}> · exp {fmtDateShort(r.expiry)}</span> : null}</>
                                : '—' },
                              { title: 'Qty', dataIndex: 'qty', align: 'right' as const, render: (v: any) => fmtQty(v) },
                            ]}
                          />
                        ),
                      }}
                      columns={[
                        { title: 'Shipment', dataIndex: 'shipmentNo' },
                        {
                          title: 'Status', dataIndex: 'status',
                          render: (v: string) => <Tag color={SHIPMENT_COLOR[v]}>{v}</Tag>,
                        },
                        { title: 'Carrier', dataIndex: 'carrier', render: (v: any) => v ?? '—' },
                        { title: 'LR', dataIndex: 'lrNo', render: (v: any) => v ?? '—' },
                        { title: 'Vehicle', dataIndex: 'vehicleNo', render: (v: any) => v ?? '—' },
                        {
                          title: 'Dispatched', dataIndex: 'dispatchedAt',
                          render: (v: any) => formatIst(v),
                        },
                        {
                          title: 'Delivered', dataIndex: 'deliveredAt',
                          render: (v: any) => formatIst(v),
                        },
                        {
                          title: 'POD', dataIndex: 'podUrl',
                          render: (v: any) => v ? <a href={v} target="_blank" rel="noopener">view</a> : '—',
                        },
                        {
                          title: '', key: 'actions', width: 180,
                          render: (_: any, s: any) => (
                            <Space>
              {/* Delivery is a staff/field action — hidden entirely for a retailer (no greyed button). */}
                              {s.status === 'DISPATCHED' && canDeliver && (
                                <Tooltip label="Mark delivered + record POD">
                                  <Button size="sm" variant="text" icon={<Flag size={16} />} onClick={() => askDeliver(s.id)}>Delivered</Button>
                                </Tooltip>
                              )}
                              {s.status !== 'DELIVERED' && s.status !== 'CANCELLED' && (
                                canEdit ? (
                                  <Popconfirm
                                    title="Cancel and return stock?"
                                    onConfirm={() => askCancel(s.id)}
                                  >
                                    <Button size="sm" variant="text" icon={<XCircle size={16} className="text-danger-600 dark:text-danger-500" />} aria-label="Cancel shipment" />
                                  </Popconfirm>
                                ) : (
                                  <GatedButton
                                    allowed={false}
                                    reason="You don't have permission to cancel shipments (requires ORDER.EDIT)"
                                    size="sm"
                                    variant="text"
                                    icon={<XCircle size={16} className="text-danger-600 dark:text-danger-500" />}
                                    aria-label="Cancel shipment"
                                  />
                                )
                              )}
                            </Space>
                          ),
                        },
                      ]}
                    />
                  )}
                  <Divider />
                  <Typography.Title level={5}>Invoices ({invoices?.length ?? 0})</Typography.Title>
                  {!invoices?.length ? (
                    <Empty description="No invoices linked to this order yet" />
                  ) : (
                    <Table
                      rowKey="id"
                      size="small"
                      dataSource={invoices}
                      pagination={false}
                      onRow={(r: any) => ({ onClick: () => navigate(`/billing/invoices/${r.id}`), style: { cursor: 'pointer' } })}
                      columns={[
                        { title: 'Invoice No', dataIndex: 'invoiceNo' },
                        { title: 'Date', dataIndex: 'invoiceDate' },
                        { title: 'Due', dataIndex: 'dueDate' },
                        { title: 'Total', dataIndex: 'grandTotal', align: 'right' as const, render: inr2 },
                        { title: 'Paid', dataIndex: 'paidAmt', align: 'right' as const, render: inr2 },
                        {
                          title: 'Balance', dataIndex: 'balanceAmt', align: 'right' as const,
                          render: (v: any) => <b style={{ color: Number(v) > 0 ? '#D9363E' : '#52C41A' }}>{inr2(v)}</b>,
                        },
                        { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag>{s}</Tag> },
                        {
                          title: 'Verification', dataIndex: 'verificationStatus',
                          render: (v: string) => {
                            const c = v === 'VERIFIED' ? 'green' : v === 'MISMATCH' ? 'red' : v === 'OVERRIDDEN' ? 'orange' : 'default';
                            return <Tag color={c}>{(v ?? 'UNVERIFIED').replace(/_/g, ' ')}</Tag>;
                          },
                        },
                        ...((canVoidInvoice || canVerify || canOverride) ? [{
                          title: 'Actions', key: 'act', align: 'right' as const, width: 230,
                          render: (_: any, inv: any) => {
                            if (inv.status === 'CANCELLED') return <Tag>Voided</Tag>;
                            return (
                              <Space onClick={(e: any) => e.stopPropagation()}>
                                {/* Only Tally-uploaded invoices (with a PDF) are gated; verify re-checks vs the order. */}
                                {canVerify && inv.pdfUrl && inv.verificationStatus !== 'VERIFIED' && inv.verificationStatus !== 'OVERRIDDEN' && (
                                  <Button size="sm" variant="text"
                                    loading={verifyInvoice.isPending && verifyInvoice.variables === String(inv.id)}
                                    onClick={() => verifyInvoice.mutate(String(inv.id))}>Verify</Button>
                                )}
                                {canOverride && inv.verificationStatus === 'MISMATCH' && (
                                  <Button size="sm" variant="soft-danger" onClick={() => setOverrideFor(String(inv.id))}>Approve mismatch</Button>
                                )}
                                {canVoidInvoice && (
                                  <Popconfirm title="Void this invoice?" description="Reverses the ledger debit. You can upload a corrected invoice afterwards." okText="Void" okButtonProps={{ danger: true }} onConfirm={() => voidInvoice.mutate(String(inv.id))}>
                                    <Button size="sm" variant="soft-danger" loading={voidInvoice.isPending && voidInvoice.variables === String(inv.id)}>Void</Button>
                                  </Popconfirm>
                                )}
                              </Space>
                            );
                          },
                        }] : []),
                      ]}
                    />
                  )}
                  {(orderNotes?.length ?? 0) > 0 && (
                    <>
                      <Divider />
                      <Typography.Title level={5}>Credit / debit notes ({orderNotes!.length})</Typography.Title>
                      <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={orderNotes}
                        onRow={(cn: any) => ({ onClick: () => setNoteDrawerId(String(cn.id)), style: { cursor: 'pointer' } })}
                        columns={[
                          { title: 'Note No', dataIndex: 'noteNo', render: (v: string) => <span className="font-medium text-brand-700 dark:text-brand-400">{v}</span> },
                          { title: 'Type', dataIndex: 'noteType', render: (v: string) => <Tag color={v === 'CREDIT' ? 'green' : 'gold'}>{v}</Tag> },
                          { title: 'Against invoice', dataIndex: 'invoiceNo', render: (v: string) => v || '—' },
                          { title: 'Date', dataIndex: 'noteDate', render: (v: any) => (v ? String(v).substring(0, 10) : '—') },
                          { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v === 'CANCELLED' ? 'red' : 'green'}>{v === 'CANCELLED' ? 'Reversed' : v}</Tag> },
                          { title: 'Amount', dataIndex: 'amount', align: 'right' as const, render: (v: any, cn: any) => <b className={cn.status === 'CANCELLED' ? 'text-subtle line-through' : undefined}>{inr2(v)}</b> },
                        ]}
                      />
                    </>
                  )}
                </>
              ),
            },
          ]}
        />
      </Card>

      <CreditNoteDrawer id={noteDrawerId} onClose={() => setNoteDrawerId(null)} />

      <DispatchModal open={dispatchOpen} onClose={() => setDispatchOpen(false)} orderId={id} />
      <CancelOrderModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        orderNo={o.orderNo}
        breakdown={cancelBreakdown}
        fmtQty={fmtQty}
        canFullCancel={canFullCancelNow}
        fullCancelBlockedByInvoice={fullCancelBlockedByInvoice}
        onFullCancel={() => change.mutate('CANCELLED')}
        fullCancelLoading={change.isPending && change.variables === 'CANCELLED'}
        canShortClose={canShortCloseNow}
        shortCloseHint={shortCloseHint}
        onShortClose={(reason) => shortClose.mutate(reason)}
        shortCloseLoading={shortClose.isPending}
      />
      <PayOrderModal open={payOpen} onClose={() => setPayOpen(false)} orderId={String(id)} orderNo={o.orderNo} />
      <SettleAdvanceModal
        open={settleOpen}
        onClose={() => setSettleOpen(false)}
        orderId={String(id)}
        orderNo={o.orderNo}
        excess={Number(advanceExcess?.excess ?? 0)}
        billed={Number(advanceExcess?.billed ?? 0)}
        paidNet={Number(advanceExcess?.paidNet ?? 0)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['order-advance-excess', id] });
          qc.invalidateQueries({ queryKey: ['order-payments', id] });
          qc.invalidateQueries({ queryKey: ['order-pay-summary', id] });
          qc.invalidateQueries({ queryKey: ['order-timeline', id] });
          qc.invalidateQueries({ queryKey: ['outstanding-aging'] });
        }}
      />
      <RefundPaymentModal
        payment={refundFor}
        orderNo={o.orderNo}
        onClose={() => setRefundFor(null)}
        onDone={() => {
          qc.invalidateQueries({ queryKey: ['order-payments', id] });
          qc.invalidateQueries({ queryKey: ['order-pay-summary', id] });
          qc.invalidateQueries({ queryKey: ['order-timeline', id] });
          qc.invalidateQueries({ queryKey: ['payments'] });
          qc.invalidateQueries({ queryKey: ['outstanding-aging'] });
        }}
      />
      <InvoiceUploadModal
        open={invoiceUploadOpen}
        onClose={() => setInvoiceUploadOpen(false)}
        presetOrder={{ id, orderNo: o.orderNo, retailerId: o.retailerId, grandTotal: o.grandTotal, orderedQty: totalOrdered, pendingQty: totalPending }}
      />
      <InvoiceUploadModal
        open={!!invoiceForShipment}
        onClose={() => setInvoiceForShipment(null)}
        presetShipment={invoiceForShipment ? { id: String(invoiceForShipment.id), shipmentNo: invoiceForShipment.shipmentNo, orderId: id, orderNo: o.orderNo, retailerId: o.retailerId } : null}
      />
      <RaiseReturnModal open={returnOpen} onClose={() => setReturnOpen(false)} order={o} invoices={invoices ?? []} shipments={shipments ?? []} />

      {/* Pick which packed dispatch to invoice — only when the header shortcut finds 2+ awaiting one. */}
      <Modal open={invoicePickerOpen} onOpenChange={(nextOpen) => { if (!nextOpen) setInvoicePickerOpen(false); }} title="Upload invoice — choose the shipment">
        <div className="text-sm" style={{ color: token.colorTextSecondary, marginBottom: 8 }}>
          More than one packed shipment is awaiting a Tally invoice. Each shipment is billed separately — pick the one to invoice.
        </div>
        {shipmentsNeedingInvoice.map((s: any) => (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 0', borderTop: '1px solid #f5f5f5' }}>
            <span><b>{s.shipmentNo}</b> <Tag>{s.status}</Tag></span>
            <Button variant="primary" size="sm" icon={<Upload size={16} />} onClick={() => { setInvoicePickerOpen(false); setInvoiceForShipment(s); }}>Upload for this</Button>
          </div>
        ))}
      </Modal>
      <ApproveOrderModal
        open={approveOpen}
        orderId={id ?? null}
        orderNo={o.orderNo}
        defaultWarehouseId={o.dispatchWarehouseId}
        onClose={() => setApproveOpen(false)}
      />
      <Modal
        open={!!overrideFor}
        title="Approve invoice mismatch"
        onOpenChange={(nextOpen) => { if (!nextOpen) { setOverrideFor(null); setOverrideReason(''); } }}
        footer={
          <>
            <Button variant="default" onClick={() => { setOverrideFor(null); setOverrideReason(''); }}>Cancel</Button>
            <Button variant="danger" loading={overrideInvoice.isPending} disabled={!overrideReason.trim()} onClick={() => overrideFor && overrideInvoice.mutate({ invId: overrideFor, reason: overrideReason.trim() })}>Approve & unblock dispatch</Button>
          </>
        }
      >
        <Typography.Paragraph type="secondary">
          The Tally invoice totals differ from the order. Approving records your reason (audit) and lets the order dispatch.
        </Typography.Paragraph>
        <Input.TextArea rows={3} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
          placeholder="Reason for approving the mismatch (mandatory)" />
      </Modal>

      {timeline && timeline.length > 0 && (
        <div className="text-xs" style={{ marginTop: 8, color: token.colorTextTertiary }}>
          <Clock size={16} /> Last activity {formatIst(timeline[timeline.length - 1].at)}
        </div>
      )}
    </div>
  );
}
